/// API の生レスポンス (`unknown`) を検証して `SiteMemberPage` にする門番
/// (parse, don't validate / #471)。
///
/// 失敗方針が他の parser と 1 点だけ違う。`profile` の中身は会員本人が mypage から
/// 書き換えられる**自己申告**なので、契約と違う型が入っていてもそのキーだけ null に落とす
/// (throw しない)。1 人が `legal_training_term` に文字列を入れただけで営業の一覧がまるごと
/// 消えるのを防ぐためで、backend も同じ方針で読んでいる (U9 の設計判断 8)。
/// D20 の運用規則「読み手は古い版を許容し、欠けたキーを null として扱う」を、信頼できない
/// 書き手が相手でも成り立つ形にしたもの。
///
/// 一方でページの構造 (`items` / `total` / `page` / `per_page`) と `user_id` は自前 backend の
/// 契約なので、破れていれば throw する (store の catch が UI のエラー表示に変換する)。

import { ResponseMappingError, reqArray, reqNumber, reqRecord } from '@shared/value-objects/parse-helpers';
import type { SiteMember, SiteMemberPage, SiteMemberProfile } from './model';

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return raw !== null && typeof raw === 'object' && !Array.isArray(raw);
}

function optString(o: Record<string, unknown>, key: string): string | null {
  const value = o[key];
  return typeof value === 'string' ? value : null;
}

/// 整数だけを通す。backend は該当キーを `Option<i64>` / `Option<u64>` で返すので、
/// 小数・NaN・Infinity が表示に乗る余地を残さない (`Number.isInteger` が 3 つとも落とす)。
function optInteger(o: Record<string, unknown>, key: string): number | null {
  const value = o[key];
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

/// `profile` が丸ごと欠けていても全キー null として読む (D20: 読み手は古い版を許容する)。
function parseProfile(raw: unknown): SiteMemberProfile {
  const o = isRecord(raw) ? raw : {};
  return {
    profile_version: optInteger(o, 'profile_version'),
    last_name: optString(o, 'last_name'),
    first_name: optString(o, 'first_name'),
    organization_name: optString(o, 'organization_name'),
    phone: optString(o, 'phone'),
    member_kind: optString(o, 'member_kind'),
    legal_training_term: optInteger(o, 'legal_training_term'),
    litigation_cases_per_month: optString(o, 'litigation_cases_per_month'),
    terms_consented_at: optString(o, 'terms_consented_at'),
    terms_version: optString(o, 'terms_version'),
    marketing_consented_at: optString(o, 'marketing_consented_at'),
    profile_completed_at: optString(o, 'profile_completed_at'),
  };
}

function parseSiteMember(raw: unknown, field: string): SiteMember {
  const o = reqRecord(raw, field);
  const userId = o.user_id;
  if (typeof userId !== 'string' || userId.length === 0) {
    throw new ResponseMappingError(`${field}.user_id`, 'expected a non-empty string');
  }
  return {
    user_id: userId,
    email: optString(o, 'email'),
    // backend は bool で返す契約。真偽以外は false に倒す — 未確認のメールを「済」と
    // 出す方が、確認済みを「未」と出すより害が大きいため。
    email_verified: o.email_verified === true,
    created_at: optString(o, 'created_at'),
    last_login: optString(o, 'last_login'),
    logins_count: optInteger(o, 'logins_count'),
    profile: parseProfile(o.profile),
  };
}

export function parseSiteMemberPage(raw: unknown): SiteMemberPage {
  const o = reqRecord(raw, 'site_member_page');
  return {
    items: reqArray(o.items, 'site_member_page.items').map((item, i) =>
      parseSiteMember(item, `site_member_page.items[${i}]`),
    ),
    total: reqNumber(o.total, 'site_member_page.total'),
    page: reqNumber(o.page, 'site_member_page.page'),
    per_page: reqNumber(o.per_page, 'site_member_page.per_page'),
  };
}
